import { createSignal, type Accessor } from 'solid-js';

/**
 * Which nodes of a tree-shaped card this viewer has open, and which one is selected (B85).
 *
 * Everything starts collapsed when the page loads. What the viewer opens and selects stays exactly as
 * they left it for as long as the page stays open — closing and reopening the sheet, leaving Effects and
 * coming back, switching the active set, another client saving the set — because it is held by the
 * provider, which lives exactly as long as the page does. Nothing is written anywhere: no storage, no
 * room document. It is only this viewer's view (H41).
 */
export interface SettingsTreeView {
  open: Accessor<ReadonlySet<string>>;
  selected: Accessor<string>;
  setOpen: (id: string, open: boolean) => void;
  select: (id: string) => void;
}

export interface SettingsTreeViewStore {
  /** The view of one card in one room, keyed `<venueId>:<roomId>:<packageId>`. */
  view: (key: string) => SettingsTreeView;
}

export function createSettingsTreeViewStore(): SettingsTreeViewStore {
  const views = new Map<string, SettingsTreeView>();
  return {
    view(key) {
      const existing = views.get(key);
      if (existing) return existing;
      const [open, setOpenIds] = createSignal<ReadonlySet<string>>(new Set());
      const [selected, setSelected] = createSignal('');
      const created: SettingsTreeView = {
        open,
        selected,
        setOpen: (id, value) => setOpenIds((current) => {
          if (current.has(id) === value) return current;
          const next = new Set(current);
          if (value) next.add(id);
          else next.delete(id);
          return next;
        }),
        select: (id) => setSelected(id),
      };
      views.set(key, created);
      return created;
    },
  };
}
